import React, { useCallback, useMemo, useState } from 'react'
import {
  PiArrowClockwise,
  PiCaretDown,
  PiGear,
  PiSparkle,
  PiUser,
} from 'react-icons/pi'
import { Link } from 'react-router-dom'
import { RecommendationsCard } from '../../../components/RecommendationsCard/RecommendationsCard'
import { PerformanceDashboardCardFbUi } from '../../../components/PerformanceDashboard/PerformanceDashboardCardFbUi'
import { QuickAccessCardFbUi } from '../../../components/QuickAccess/QuickAccessCardFbUi'
import {
  demoResetSummary,
  type ActionSummaryData,
} from '../../../services/recommendations_api'
import { useQueryClient } from '@tanstack/react-query'
import { useAuth } from '../../../context/authentication/useAuth'
import { useUsers } from '../../../services/users'
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
  HelpPopover,
  Link as UiLink,
  OverlayScopeProvider,
  TabRoot,
  TabList,
  TabTrigger,
  TabContent,
} from '@firstbite/ui'
import { Alert } from '../../../components/FbUI/Alert'
import { ErrorBoundary } from '../../../components/UI/ErrorBoundary'
import { useFeatureFlagService } from '../../../utils/useFeatureFlagService'
import { buildSummaryPickerItems } from './summaryPicker'
import type { RightPanelState } from '../../../components/RecommendationsCard/RightPanel'
import confirm from '../../../components/dialogConfirm'

/**
 * Home page - Your daily recommendations and priorities
 *
 * Single centered column: greeting + controls → Quick Access widgets →
 * a Performance | Action Center tab section (Action Center is entitlement-
 * gated; when off, the section is a single Performance tab). Width is
 * constrained with a max-width container so dashboards stay readable on wide
 * displays.
 *
 * Superusers can select any user to view/generate their recommendations.
 */
export function HomePage() {
  const [isGenerating, setIsGenerating] = useState(false)
  const [selectedUserId, setSelectedUserId] = useState<number | undefined>()
  const queryClient = useQueryClient()
  const { user, isAdmin, isLimited } = useAuth()
  const { enableQuickAccess, enableActionCenter } = useFeatureFlagService()
  const { data: users } = useUsers(undefined, {
    query: { enabled: (user?.is_superuser || isAdmin) && !isLimited },
  })

  const isSuperuser = user?.is_superuser
  const canManageSummaries = isSuperuser || isAdmin

  // Action Center is entitlement-gated (`enableActionCenter`; a future paid
  // upsell). It drives the tab frame below: on → Performance | Action Center
  // two-up; off → the frame collapses to a single Performance tab (the tab
  // header persists either way, so there's no layout jump when it's granted).
  const showActionCenter = enableActionCenter

  // Local-time greeting: morning < 12, afternoon < 18, else evening.
  // Computed once per render rather than memoized — Date() is cheap and
  // we don't want a stale greeting if the user keeps the tab open
  // across the noon/6pm boundary (a re-render close to that time will
  // pick up the new band naturally).
  const greeting =
    new Date().getHours() < 12
      ? 'Good morning!'
      : new Date().getHours() < 18
        ? 'Good afternoon!'
        : 'Good evening!'

  const summaryQueryKey = useMemo(
    () => ['recommendations', selectedUserId ?? 'self'],
    [selectedUserId]
  )

  const handleDemoReset = async () => {
    const targetLabel = selectedUserId
      ? (selectedUser?.email ?? 'this user')
      : (user?.email ?? 'your account')
    const confirmed = await confirm(
      <>
        This wipes all recommendations for <strong>{targetLabel}</strong> and
        replays the demo seed in their place. Any existing recommendation state
        (applied actions, dismissals, in-flight LLM evaluations) is lost. Run
        only against demo accounts.
      </>,
      'Reset demo data?',
      'This cannot be undone.',
      { confirmLabel: 'Reset demo data', buttonVariant: 'danger' }
    )
    if (!confirmed) return

    setIsGenerating(true)
    try {
      await demoResetSummary(selectedUserId)
      // Invalidate to force a fresh fetch — the reset is synchronous so
      // the DB already has the new summary.
      await queryClient.invalidateQueries({ queryKey: summaryQueryKey })
    } catch (error) {
      console.error('Failed to reset demo:', error)
    } finally {
      setIsGenerating(false)
    }
  }

  const handleSummaryLoaded = useCallback(
    (summary: ActionSummaryData | null) => {
      if (summary?.status === 'COMPLETED' || summary?.status === 'FAILED') {
        setIsGenerating(false)
      }
    },
    []
  )

  const userDropdownItems = useMemo(
    () => buildSummaryPickerItems(users, user?.id),
    [users, user]
  )

  const selectedUser =
    users?.find((u) => u.id === selectedUserId) ??
    (selectedUserId === user?.id ? user : undefined)

  const selectedUserLabel = selectedUserId
    ? (userDropdownItems.find((o) => o.key === String(selectedUserId))?.label ??
      'My Summary')
    : 'My Summary'

  // Single panel-at-a-time state. Setting a new ``kind`` replaces whatever was
  // open. RecommendationsCard reads this to open its surface-scoped fb-ui Sheets
  // (recently-completed + apply-rec) — both portal into the content region via
  // the OverlayScopeProvider below, so there's no bespoke sider host anymore.
  const [activePanel, setActivePanel] = useState<RightPanelState>({
    kind: 'closed',
  })
  // The content-region element the surface-scoped panels portal into + fill.
  // Callback ref → state so the provider re-renders with the node once mounted.
  const [surfaceEl, setSurfaceEl] = useState<HTMLDivElement | null>(null)

  return (
    <div className="flex flex-col flex-1 min-h-0">
      <div
        ref={setSurfaceEl}
        className="flex-1 bg-fbui-gray-50 overflow-hidden relative"
      >
        <OverlayScopeProvider container={surfaceEl}>
          {/* Scrollable content layer. Constrained to the relative
            wrapper so the sider that overlays it (below) doesn't
            scroll with content. */}
          <div className="absolute inset-0 overflow-auto">
            <div className="max-w-7xl mx-auto py-4 px-6 lg:px-8">
              <div className="space-y-4">
                {/* Page header card — greeting + page-level controls.
                  Kept separate from the performance card so each widget
                  has a single, clear purpose. */}
                {/* px-3 matches the minimal TabTrigger's px-[0.75rem] so the
                  greeting's left edge lines up with the "Performance"/"Action
                  Center" tab labels below. */}
                <div className="px-3 py-3 flex items-center justify-between gap-4">
                  <h1 className="text-2xl font-normal text-fg">{greeting}</h1>
                  <div className="flex items-center gap-2">
                    {isSuperuser && (
                      <Button
                        variant="ghost"
                        size="md"
                        // Red ghost = low-chrome but still reads as destructive.
                        // fb-ui has no danger-ghost variant, so tint the neutral
                        // ghost with fb-ui red tokens. (Demo-instance only.)
                        className="text-fbui-red-600 hover:bg-fbui-red-50 hover:text-fbui-red-700"
                        onClick={handleDemoReset}
                        disabled={isGenerating}
                        leftIcon={
                          <PiArrowClockwise
                            className={isGenerating ? 'animate-spin' : ''}
                          />
                        }
                        title={
                          selectedUserId
                            ? `Reset demo data for ${selectedUser?.email}. This wipes their recommendations and seeds fresh fixtures.`
                            : 'Reset demo data — wipes recommendations and seeds fresh fixtures.'
                        }
                      >
                        Reset User Demo Data
                      </Button>
                    )}
                    {canManageSummaries && users && (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="primary"
                            appearance="outline"
                            size="md"
                            leftIcon={<PiUser />}
                            rightIcon={<PiCaretDown />}
                          >
                            {selectedUserLabel}
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent
                          align="end"
                          // Cap at ~6 rows, then scroll (the built-in max-h is the
                          // full viewport height; the user list can run to ~20).
                          className="max-h-[200px]"
                        >
                          <DropdownMenuRadioGroup
                            value={
                              selectedUserId !== undefined
                                ? String(selectedUserId)
                                : 'self'
                            }
                            onValueChange={(key) => {
                              if (key === 'self') {
                                setSelectedUserId(undefined)
                              } else {
                                const id = Number(key)
                                setSelectedUserId(
                                  id === user?.id ? undefined : id
                                )
                              }
                              setIsGenerating(false)
                            }}
                          >
                            {userDropdownItems.map((item) => (
                              <DropdownMenuRadioItem
                                key={item.key}
                                value={item.key}
                              >
                                {item.label}
                              </DropdownMenuRadioItem>
                            ))}
                          </DropdownMenuRadioGroup>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                  </div>
                </div>

                {/* Quick Access — Pinned, Recently visited, Upcoming. Migrated to
                  @firstbite/ui (replaced the legacy QuickAccessCard). Gated on
                  enableQuickAccess (basics-tier feature). */}
                {enableQuickAccess && <QuickAccessCardFbUi />}

                {/* Performance | Action Center as tabs. The tab frame persists
                  even when Action Center is gated off — it then collapses to a
                  single Performance tab (no layout jump when it's granted). Both
                  panels stay mounted (TabContent hides, doesn't unmount), so the
                  page-level drawer portal (below) and each section's fetched data
                  survive tab switches. */}
                <TabRoot
                  defaultValue="performance"
                  variant="minimal"
                  // Close an open recommendation drawer when leaving a tab so it
                  // doesn't linger over the other panel. (Mostly a keyboard-nav
                  // guard — a mouse tab-click hits the drawer's backdrop first.)
                  onValueChange={() => {
                    if (activePanel.kind !== 'closed')
                      setActivePanel({ kind: 'closed' })
                  }}
                >
                  <TabList aria-label="Homepage sections">
                    <TabTrigger value="performance" className="text-base">
                      Performance
                    </TabTrigger>
                    {showActionCenter && (
                      // Leading Sparkle = the fb-ui AI mark; via the tab's icon
                      // slot it inherits the tab color (gray at rest, brand-green
                      // when active) and flags this as the AI-powered section.
                      <TabTrigger
                        value="actionCenter"
                        className="text-base"
                        // `!h-4 !w-4` overrides the tab's `[&_svg]:size-5` (which
                        // reads too large next to the label) down to ~label size.
                        icon={<PiSparkle className="!h-4 !w-4" />}
                      >
                        Action Center
                      </TabTrigger>
                    )}
                  </TabList>

                  <TabContent value="performance">
                    <PerformanceDashboardCardFbUi userId={selectedUserId} />
                  </TabContent>

                  {showActionCenter && (
                    <TabContent value="actionCenter">
                      <div className="bg-fbui-base-white rounded-xl border border-fbui-gray-200 shadow-fbui-sm pt-4 px-4 pb-0 overflow-hidden">
                        <ErrorBoundary
                          fallback={
                            <Alert
                              color="danger"
                              centered
                              className="mb-4"
                              title="Failed to load recommendations"
                              description="Please try refreshing the page"
                            />
                          }
                        >
                          <RecommendationsCard
                            userId={selectedUserId}
                            onSummaryLoaded={handleSummaryLoaded}
                            activePanel={activePanel}
                            onActivePanelChange={setActivePanel}
                            headerTrailingSlot={
                              <div className="flex items-center gap-2">
                                {/* fb-ui Link (basil brand) via `asChild` so it
                                  keeps router navigation. */}
                                <UiLink asChild variant="primary" size="sm">
                                  <Link to="/settings/ai">
                                    <PiGear aria-hidden="true" />
                                    Manage AI Agents
                                  </Link>
                                </UiLink>
                                {/* "What is this?" for a new feature — trails
                                  Manage AI Agents (both relate to the agents that
                                  power the Action Center). */}
                                <HelpPopover
                                  triggerSize="sm"
                                  size="md"
                                  label="About the Action Center"
                                  title="Action Center"
                                  // Trigger sits top-right — open leftward so the
                                  // panel doesn't run off the right screen edge.
                                  align="end"
                                >
                                  Your AI agents scan your accounts and surface
                                  the next actions that need attention — replies
                                  to send, deals to nudge, follow-ups due —
                                  ranked by urgency. Group them by priority,
                                  type, or company, and choose which agents run
                                  under Manage AI Agents.
                                </HelpPopover>
                              </div>
                            }
                          />
                        </ErrorBoundary>
                      </div>
                    </TabContent>
                  )}
                </TabRoot>
              </div>
            </div>
          </div>
        </OverlayScopeProvider>
      </div>
    </div>
  )
}
